import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * Per-route SEO component.
 *
 * Renders <title>, meta description, canonical URL, Open Graph tags,
 * Twitter card tags, optional robots directives, and any number of
 * JSON-LD structured-data blocks (passed via the `jsonLd` prop).
 *
 * All values are optional. We fill in sensible Valor defaults so callers
 * can pass only what differs (commonly title + description + path).
 */
const SITE_NAME = "Valor Tactical";
const DEFAULT_DESC =
  "Valor Tactical builds mission-critical defense hardware and energetics — engineered, manufactured, and qualified in the United States.";
const DEFAULT_OG_IMAGE =
  "/og-default.png";

// The single, canonical origin for the production site. Every <link
// rel="canonical"> and og:url tag is stamped against this so a visit to
// https://www.valortactical.us/... or the preview URL still tells search
// engines that the authoritative page lives on the apex domain.
const CANONICAL_ORIGIN = "https://valortactical.us";

function originFromEnv() {
  // For search / social crawlers we ALWAYS advertise the canonical apex —
  // never the www subdomain and never the preview origin. This is what
  // consolidates SEO signals to a single URL.
  return CANONICAL_ORIGIN;
}

export default function Seo({
  title,
  description = DEFAULT_DESC,
  path,
  image = DEFAULT_OG_IMAGE,
  imageAlt,
  type = "website",
  keywords,
  noindex = false,
  jsonLd, // single object OR array of objects
}) {
  const origin = originFromEnv();
  const url = path ? `${origin}${path.startsWith("/") ? "" : "/"}${path}` : origin;
  // Idempotent: only append "| Valor Tactical" if the caller didn't already.
  // Prevents double-suffixing like "M205 … | Valor Tactical | Valor Tactical".
  const fullTitle = title
    ? (title.trim().endsWith(`| ${SITE_NAME}`) || title.trim().endsWith(`— ${SITE_NAME}`)
        ? title
        : `${title} | ${SITE_NAME}`)
    : `${SITE_NAME} — Defense Manufacturing, Energetics & Mission-Critical Hardware`;

  // LinkedIn and some other platforms do not show WebP previews; every local
  // .webp has a .jpg sibling generated for sharing.
  const shareImage = image && image.startsWith("/") && image.endsWith(".webp") ? image.replace(/\.webp$/, ".jpg") : image;
  const absoluteImage = shareImage && new URL(shareImage, `${origin}/`).href;

  const ldArr = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      {absoluteImage && <meta property="og:image" content={absoluteImage} />}
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {absoluteImage && <meta name="twitter:image" content={absoluteImage} />}
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {ldArr.map((obj, i) => (
        <script
          key={obj["@id"] || obj["@type"] ? `${obj["@type"]}-${obj["@id"] || i}` : `ld-${i}`}
          type="application/ld+json"
          // Stringify here so each block is its own valid JSON-LD payload.
          // The `.replace(/</g, ...)` escapes any `<` (in particular `</script>`)
          // that may arrive from third-party feeds like Paylocity job descriptions,
          // preventing tag-break-out and DOM-XSS via structured data (SEC-003).
          // The \u003c form is still a valid JSON string but browsers can't
          // parse it as an HTML tag opener.
          // eslint-disable-next-line react/no-danger
        >
          {JSON.stringify(obj).replace(/</g, "\\u003c")}
        </script>
      ))}
    </Helmet>
  );
}
